import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { parseNumericValue } from '@/services/pmt2Utils';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableRow, TableHead, TableHeader } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { LinkIcon, CheckCircle, AlertCircle } from 'lucide-react'; // Import icons
import { cn } from "@/lib/utils"; // Import cn utility
import { fetchLatestOldValuesMap, fetchLatestLedgerEntryDate } from '@/services/pmt3OldValuesService';
import { fetchMaxBidAdjustmentData, fetchLastUpdateDaysAgo } from '@/services/pmt3DashboardDataService'; // Import new service functions

// Reusable fetchClientUrl function (consider moving to a shared util if used elsewhere)
const fetchClientUrl = async (companyName: string, docId: string): Promise<string | null> => {
    if (!companyName || !docId) return null;
    try {
        // Sanitize docId before using it in the path - crucial for keys with spaces/periods
        const sanitizedDocId = docId.replace(/\//g, '_').replace(/\./g, '_');
        const urlDocRef = doc(db, 'pmt2_clienturldatabase', companyName, 'items', sanitizedDocId);
        const urlDocSnap = await getDoc(urlDocRef);
        return urlDocSnap.exists() ? urlDocSnap.data()?.value ?? null : null;
    } catch (error) {
        console.error(`Error fetching client URL for ${docId}:`, error);
        return null;
    }
};

interface GlobalBidsDashboardProps {
  product: PMT2Product | null;
}

type OldValuesMap = Record<string, string>;

const GlobalBidsDashboard: React.FC<GlobalBidsDashboardProps> = ({ product }) => {
  const [oldValuesMap, setOldValuesMap] = useState<OldValuesMap | null>(null);
  const [maxBidAdjustmentData, setMaxBidAdjustmentData] = useState<string | null>(null);
  const [lastUpdateDaysAgoData, setLastUpdateDaysAgoData] = useState<number | null>(null);
  const [maxBidOverrideDate, setMaxBidOverrideDate] = useState<Timestamp | null>(null);
  const [defaultBidOverrideDate, setDefaultBidOverrideDate] = useState<Timestamp | null>(null);
  const [farmingBidOverrideDate, setFarmingBidOverrideDate] = useState<Timestamp | null>(null);
  const [termLevelOverridesUrl, setTermLevelOverridesUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // --- Formatting & Utility Helpers ---
  const formatCurrency = (value: any, decimals = 2): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
  };

   const formatInteger = (value: any): string => {
    const num = parseNumericValue(value);
    if (num === null) return '-';
    return Math.round(num).toLocaleString('en-US'); // Round and format
  };

  const formatDefault = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const calculateDaysAgo = (timestamp: Timestamp | null | undefined): number | null => {
      if (!timestamp || !(timestamp instanceof Timestamp)) return null;
      const now = new Date();
      const pastDate = timestamp.toDate();
      // Set both dates to midnight UTC to compare just the date part
      now.setUTCHours(0, 0, 0, 0);
      pastDate.setUTCHours(0, 0, 0, 0);
      const diffTime = now.getTime() - pastDate.getTime();
      if (diffTime < 0) return 0; // Handle future dates if necessary
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
  };
  // --- End Helpers ---

  // Fetch All Data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      // Reset all state
      setOldValuesMap(null);
      setMaxBidAdjustmentData(null);
      setLastUpdateDaysAgoData(null);
      setMaxBidOverrideDate(null);
      setDefaultBidOverrideDate(null);
      setFarmingBidOverrideDate(null);
      setTermLevelOverridesUrl(null);

      if (product?.companyName && product?.id) {
        const company = product.companyName;
        const asin = product.id;
        const termLevelOverridesDocId = 'Term Level Bid Overrides';

        try {
          const results = await Promise.allSettled([
            fetchLatestOldValuesMap(company, asin), // 0
            fetchMaxBidAdjustmentData(company, asin), // 1
            fetchLastUpdateDaysAgo(company, asin), // 2
            fetchLatestLedgerEntryDate(company, asin, 'Max Bid Override'), // 3
            fetchLatestLedgerEntryDate(company, asin, 'Default Bid Override'), // 4
            fetchLatestLedgerEntryDate(company, asin, 'Farming Bid Override'), // 5 - Check key if different
            fetchClientUrl(company, termLevelOverridesDocId) // 6
          ]);

          // Process results
          if (results[0].status === 'fulfilled') setOldValuesMap(results[0].value);
          else console.error("Error fetching old values map:", results[0].reason);

          if (results[1].status === 'fulfilled') setMaxBidAdjustmentData(results[1].value);
          else console.error("Error fetching max bid adjustment data:", results[1].reason);

          if (results[2].status === 'fulfilled') setLastUpdateDaysAgoData(results[2].value);
          else console.error("Error fetching last update days ago:", results[2].reason);

          if (results[3].status === 'fulfilled') setMaxBidOverrideDate(results[3].value);
          else console.error("Error fetching Max Bid Override date:", results[3].reason);

          if (results[4].status === 'fulfilled') setDefaultBidOverrideDate(results[4].value);
          else console.error("Error fetching Default Bid Override date:", results[4].reason);

          if (results[5].status === 'fulfilled') setFarmingBidOverrideDate(results[5].value);
          else console.error("Error fetching Farming Bid Override date:", results[5].reason);

          if (results[6].status === 'fulfilled') setTermLevelOverridesUrl(results[6].value);
          else console.error("Error fetching Term Level Overrides URL:", results[6].reason);

        } catch (error) {
          console.error("Error fetching Global Bids dashboard data:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [product]);

  // Configuration for dashboard items
  const globalBidsConfig = [
    { label: 'Max Bid', valueKey: 'Max Bid', oldValueKey: 'Max Bid', overrideValueKey: 'Max Bid Override', oldOverrideValueKey: 'Max Bid Override', overrideDateStateKey: 'maxBidOverrideDate', format: 'currency' },
    { label: 'Default Bid', valueKey: 'Default Bid', oldValueKey: 'Default Bid', overrideValueKey: 'Default Bid Override', oldOverrideValueKey: 'Default Bid Override', overrideDateStateKey: 'defaultBidOverrideDate', format: 'currency' },
    { label: 'Default Farming Bid', valueKey: 'Default Farming Bid', oldValueKey: 'Default Farming Bid', overrideValueKey: 'Farming Bid Override', oldOverrideValueKey: 'Farming Bid Override', overrideDateStateKey: 'farmingBidOverrideDate', format: 'currency' }, // Check override keys carefully
    { label: 'Term Level Bid Overrides', valueKey: 'Term Level Bid Overrides', oldValueKey: 'Term Level Bid Overrides', format: 'integer', linkUrl: termLevelOverridesUrl },
    { label: 'Max Bid Adjustments Needed', valueKey: null, source: maxBidAdjustmentData }, // Use source for fetched data
    { label: 'Last Bid Update Days Ago', valueKey: 'PPC Status', source: lastUpdateDaysAgoData, format: 'daysAgoLogic' }, // Special logic needed
  ];

  // Helper to format display values
  const formatDisplay = (value: any, formatType?: string): string => {
      if (value === null || value === undefined || value === '') return '-';
      switch (formatType) {
          case 'currency':
              return formatCurrency(value);
          case 'integer':
              return formatInteger(value);
          // Add other formats if needed
          default:
              return formatDefault(value);
      }
  };

  // Helper to get the final value for "Last Bid Update Days Ago"
  const getLastBidUpdateDisplay = (): string => {
      const ppcStatus = product?.['PPC Status']?.toLowerCase();
      if (ppcStatus?.includes('pause')) return "Paused";
      if (ppcStatus?.includes('ineligible')) return "Ineligible";
      // Use the fetched 'Days ago' value from pmt2_last_update
      if (lastUpdateDaysAgoData !== null) return String(lastUpdateDaysAgoData);
      return "Never";
  };


  if (!product) {
       return (
            <Card>
                <CardHeader><CardTitle className="text-2xl font-bold">Global Bids</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-500">Select a product to view Global Bids.</p></CardContent>
            </Card>
        );
  }

  // Map state keys to actual state variables for easier access in loop
  const overrideDates: { [key: string]: Timestamp | null } = {
      maxBidOverrideDate,
      defaultBidOverrideDate,
      farmingBidOverrideDate,
  };

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">Global Bids</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="w-1/3">Item</TableHead>
                    <TableHead className="text-right">Value</TableHead>
                    <TableHead className="text-right">Old Value</TableHead>
                    <TableHead className="text-right">Override</TableHead>
                    <TableHead className="text-right">Old Override</TableHead>
                    <TableHead className="text-right">Override Days Ago</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {globalBidsConfig.map((item) => {
                    // Determine current value based on source or product key
                    const currentValue = item.source !== undefined ? item.source : (item.valueKey ? product[item.valueKey] : undefined);
                    let displayValue: string | React.ReactNode;

                    // Special formatting/logic for specific rows
                    if (item.label === 'Last Bid Update Days Ago') {
                        displayValue = getLastBidUpdateDisplay();
                    } else {
                        displayValue = formatDisplay(currentValue, item.format);
                    }

                    const oldValue = item.oldValueKey ? oldValuesMap?.[item.oldValueKey] : undefined;
                    const formattedOldValue = formatDisplay(oldValue, item.format);
                    // Compare formatted display value with formatted old value
                    const displayOldValue = formattedOldValue !== '-' && String(displayValue) !== formattedOldValue;

                    const currentOverrideValue = item.overrideValueKey ? product[item.overrideValueKey] : undefined;
                    const displayOverrideValue = formatDisplay(currentOverrideValue, item.format);
                    const showCurrentOverrideHighlight = item.overrideValueKey && displayOverrideValue !== '-';

                    const oldOverrideValue = item.oldOverrideValueKey ? oldValuesMap?.[item.oldOverrideValueKey] : undefined;
                    const formattedOldOverrideValue = formatDisplay(oldOverrideValue, item.format);
                    const highlightOldOverride = formattedOldOverrideValue !== '-' && displayOverrideValue !== formattedOldOverrideValue;
                    const showOldOverride = formattedOldOverrideValue !== '-';

                    // Get the correct override date from state using the key defined in config
                    const overrideTimestamp = item.overrideDateStateKey ? overrideDates[item.overrideDateStateKey] : null;
                    const daysAgo = calculateDaysAgo(overrideTimestamp);
                    // Display days ago only if a current override value exists
                    const overrideExists = currentOverrideValue !== null && currentOverrideValue !== undefined && currentOverrideValue !== '' && currentOverrideValue !== '-';
                    const displayDaysAgo = overrideExists && daysAgo !== null ? `${daysAgo} days` : '-';

                    const isLink = item.linkUrl && item.label === 'Term Level Bid Overrides';

                    return (
                        <TableRow key={item.label} className={cn(
                            {'border-b border-gray-200 dark:border-gray-700': ['Default Farming Bid', 'Max Bid Adjustments Needed'].includes(item.label)}
                        )}>
                            <TableCell className="font-medium py-1 px-2">
                                {isLink ? (
                                    <a href={item.linkUrl ?? '#'} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline" title="Term Level Bid Overrides Sheet">
                                        {item.label}
                                    </a>
                                ) : (
                                    item.label
                                )}
                            </TableCell>
                            <TableCell className={cn("text-right py-1 px-2", // Added padding
                                // Conditional Text Color for 'Term Level Bid Overrides'
                                item.label === 'Term Level Bid Overrides' && !isLoading && (
                                    parseNumericValue(currentValue) === 0 ? 'text-green-700 dark:text-green-400' :
                                    parseNumericValue(currentValue) !== null && parseNumericValue(currentValue)! > 0 ? 'text-yellow-500 dark:text-yellow-400' : ''
                                ),
                                // Conditional Text Color for 'Last Bid Update Days Ago'
                                item.label === 'Last Bid Update Days Ago' && !isLoading && (
                                    displayValue === 'Paused' ? 'text-yellow-500 dark:text-yellow-400' :
                                    typeof displayValue === 'string' && !isNaN(parseInt(displayValue)) && parseInt(displayValue) < 5 ? 'text-green-700 dark:text-green-400' :
                                    typeof displayValue === 'string' && !isNaN(parseInt(displayValue)) && parseInt(displayValue) > 14 ? 'text-red-600 dark:text-red-400' :
                                    typeof displayValue === 'string' && !isNaN(parseInt(displayValue)) && parseInt(displayValue) > 7 ? 'text-yellow-500 dark:text-yellow-400' : ''
                                )
                            )}>
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    <div className="flex items-center justify-end space-x-1">
                                        {/* Conditional Icon Logic */}
                                        {!isLoading && item.label === 'Term Level Bid Overrides' && parseNumericValue(currentValue) === 0 && <CheckCircle size={16} className="text-green-700 dark:text-green-400" />}
                                        {!isLoading && item.label === 'Last Bid Update Days Ago' && typeof displayValue === 'string' && !isNaN(parseInt(displayValue)) && parseInt(displayValue) < 5 && <CheckCircle size={16} className="text-green-700 dark:text-green-400" />}
                                        {!isLoading && item.label === 'Last Bid Update Days Ago' && typeof displayValue === 'string' && !isNaN(parseInt(displayValue)) && parseInt(displayValue) > 14 && <AlertCircle size={16} className="text-red-600 dark:text-red-400" />}
                                        <span>{displayValue}</span>
                                    </div>
                                )}
                            </TableCell>
                            <TableCell className={cn("text-right py-1 px-2", // Added padding
                                // Conditional Text Color for 'Last Bid Update Days Ago' - Old Value
                                item.label === 'Last Bid Update Days Ago' && !isLoading && formattedOldValue !== '-' && (
                                    formattedOldValue === 'Yes' ? 'text-green-700 dark:text-green-400' :
                                    formattedOldValue === 'No' ? 'text-yellow-500 dark:text-yellow-400' :
                                    'text-orange-600 dark:text-orange-400' // Orange text for other non-dash values
                                )
                            )}>
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    displayOldValue ? (
                                        // For 'Last Bid Update Days Ago', show only text (color applied to cell)
                                        item.label === 'Last Bid Update Days Ago' ? (
                                            <span>{formattedOldValue}</span>
                                        ) : (
                                        // For other rows, show orange highlight span
                                        <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                            {formattedOldValue}
                                        </span>
                                        )
                                    ) : (
                                        <span>{formattedOldValue}</span> // Show '-' or non-changed value
                                    )
                                )}
                            </TableCell>
                            <TableCell className="text-right py-1 px-2"> {/* Added padding */}
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    item.overrideValueKey && showCurrentOverrideHighlight ? (
                                         <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
                                             {displayOverrideValue}
                                         </span>
                                     ) : (
                                         // Remove '-' for specific rows in Override column
                                         ['Term Level Bid Overrides', 'Max Bid Adjustments Needed', 'Last Bid Update Days Ago'].includes(item.label) && displayOverrideValue === '-' ? '' : <span>{item.overrideValueKey ? displayOverrideValue : '-'}</span>
                                     )
                                )}
                            </TableCell>
                             <TableCell className="text-right py-1 px-2"> {/* Added padding */}
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    item.oldOverrideValueKey && showOldOverride ? (
                                        <span className={highlightOldOverride ? "bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100" : ""}>
                                            {formattedOldOverrideValue}
                                        </span>
                                    ) : (
                                        // Remove '-' for specific rows in Old Override column
                                        ['Term Level Bid Overrides', 'Max Bid Adjustments Needed', 'Last Bid Update Days Ago'].includes(item.label) && formattedOldOverrideValue === '-' ? '' : <span>-</span>
                                    )
                                )}
                            </TableCell>
                             <TableCell className="text-right py-1 px-2"> {/* Added padding */}
                                {/* Display days ago based on fetched date */}
                                {isLoading ? <Skeleton className="h-4 w-16 float-right" /> : (
                                    // Remove '-' for specific rows in Override Days Ago column
                                    ['Term Level Bid Overrides', 'Max Bid Adjustments Needed', 'Last Bid Update Days Ago'].includes(item.label) && displayDaysAgo === '-' ? '' : (item.overrideValueKey ? displayDaysAgo : '-')
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default GlobalBidsDashboard;